// otaUpdater.js — native-only, fully fail-safe over-the-air JS updates.
//
// Most Sirat changes are JavaScript. Rather than a full TestFlight build for
// each, the Cloudflare Pages deploy of `main` emits a JS bundle + manifest at
// https://sirat.co.za/ota/ (see scripts/make-ota.js). This pulls the latest
// bundle and applies it ON THE SAME LAUNCH via set() (one open + a brief
// reload) — so JS changes reach the phone in seconds, anywhere, with no
// TestFlight cycle. Native/Swift changes still require a TestFlight build (they
// aren't in the JS bundle).
//
// Reliability: the check runs on mount AND every time the app comes to the
// foreground (iOS resumes a warm webview without remounting React, so a
// mount-only check missed most reopens — the #1 reason updates "never came").
//
// Safety: Capgo auto-rolls-back any bundle that fails to call notifyAppReady()
// after it boots, and we never auto-retry a version that already failed. The
// downloaded bundle is cached locally, so the app stays fully offline-capable.

import { CapacitorUpdater } from "@capgo/capacitor-updater";

const MANIFEST_URL = "https://www.sirat.co.za/ota/manifest.json";
const LAST_TRIED_KEY = "sirat-ota-last-tried";
const STATUS_KEY = "sirat-ota-status"; // last-known {current, latest, checkedAt} for the UI

function isNative() {
  try { return !!(window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform()); }
  catch { return false; }
}

function writeStatus(patch) {
  try {
    const prev = JSON.parse(localStorage.getItem(STATUS_KEY) || "{}");
    localStorage.setItem(STATUS_KEY, JSON.stringify({ ...prev, ...patch }));
  } catch {}
}

async function currentVersion() {
  try {
    const cur = await CapacitorUpdater.current().catch(() => null);
    return (cur && cur.bundle && cur.bundle.version) || "builtin";
  } catch { return "builtin"; }
}

// Read-only status for the settings UI: what we're running vs what's published,
// without applying anything. Cheap (one fetch). Returns null off-native.
export async function getOtaStatus() {
  if (!isNative()) return null;
  const current = await currentVersion();
  let latest = null;
  try {
    const res = await fetch(`${MANIFEST_URL}?t=${Date.now()}`, { cache: "no-store" });
    if (res.ok) { const m = await res.json(); latest = (m && m.version) || null; }
  } catch {}
  const checkedAt = Date.now();
  const upToDate = latest != null && latest === current;
  writeStatus({ current, latest, checkedAt });
  return { current, latest, checkedAt, upToDate };
}

// The core check+apply. Fetches the manifest; if a newer bundle is published,
// downloads it and applies it IMMEDIATELY via set() (reloads the webview).
// `force: true` bypasses the failed-version guard (for the manual button).
// Returns one of: "updating" (about to reload), "uptodate", "offline",
// "skipped" (failed before — manual only), "error".
export async function checkForOtaUpdate({ force = false } = {}) {
  if (!isNative()) return "uptodate";
  try {
    const curVer = await currentVersion();
    const res = await fetch(`${MANIFEST_URL}?t=${Date.now()}`, { cache: "no-store" });
    if (!res.ok) return "offline";
    const m = await res.json();
    if (!m || !m.version || !m.url) return "offline";
    writeStatus({ current: curVer, latest: m.version, checkedAt: Date.now() });
    if (m.version === curVer) return "uptodate";

    if (!force) {
      let lastTried = null;
      try { lastTried = localStorage.getItem(LAST_TRIED_KEY); } catch {}
      if (lastTried && lastTried !== m.version) {
        // Different version — clear the stale entry so we can try this one
        try { localStorage.removeItem(LAST_TRIED_KEY); lastTried = null; } catch {}
      }
      if (lastTried === m.version) return "skipped"; // failed to boot before — don't loop
    }

    const bundle = await CapacitorUpdater.download({ url: m.url, version: m.version });
    if (!bundle || !bundle.id) return "error";
    try { localStorage.setItem(LAST_TRIED_KEY, m.version); } catch {}

    // Apply NOW — set() swaps the bundle and reloads the webview on this same
    // launch. No code after set() runs. The reloaded bundle re-runs
    // initOtaUpdater → notifyAppReady() confirms it; a bundle that fails to
    // boot is auto-reverted by Capgo and LAST_TRIED stops an auto re-try.
    await CapacitorUpdater.set({ id: bundle.id });
    return "updating";
  } catch {
    return "error"; // never block the app
  }
}

let _wired = false;
export async function initOtaUpdater() {
  if (!isNative()) return;

  // Confirm the bundle that just booted is healthy. For an OTA bundle this
  // "commits" it (cancels Capgo's auto-revert timer); harmless for builtin.
  // Must run early so the revert window doesn't lapse.
  try { await CapacitorUpdater.notifyAppReady(); } catch {}

  // Check now (cold start)…
  checkForOtaUpdate();

  // …and on every foreground. iOS resumes a warm webview without remounting
  // React, so without this most "close & reopen" cycles never re-checked —
  // the main reason updates seemed to "never come". `visibilitychange` fires
  // reliably on iOS WKWebView when the app backgrounds/foregrounds (no native
  // plugin needed); `focus`/`pageshow` are belt-and-braces. Throttle so a
  // rapid flurry of events only triggers one network check. Wire once.
  if (_wired) return;
  _wired = true;
  let lastCheck = 0;
  const recheck = () => {
    const now = Date.now();
    if (now - lastCheck < 4000) return; // throttle bursts
    lastCheck = now;
    checkForOtaUpdate();
  };
  try {
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") recheck();
    });
    window.addEventListener("focus", recheck);
    window.addEventListener("pageshow", recheck);
  } catch {}
}
